import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { Switch } from '@/components/ui/switch';
import useCurrentUser from '@/components/user/useCurrentUser';
import {
  EMPLOYEE_PAGE_ROLES,
  MANAGEMENT_ROLES,
  type EmployeePageRole,
} from '@/lib/AuthorizedRoles';

import { AvatarDropdown } from './AvatarDropdown';

import { useSettings } from '@/hook/useSettings';
import { useEmployees } from '@/hooks/useEmployees';
import { employeeFormSchema, type EmployeeFormValues } from './employee-schema';

interface EmployeeDialogProps {
  open: boolean;
  onClose: () => void;
  employee?: EmployeeFormValues;
}

export function EmployeeDialog({ open, onClose, employee }: EmployeeDialogProps) {
  const { createEditEmployee, isCreatingOrEditing } = useEmployees();
  const { settingsData } = useSettings();
  const { currentUser } = useCurrentUser();

  // Check if user has permission to view commission fields
  const canViewAuthorize =
    currentUser?.empRole &&
    EMPLOYEE_PAGE_ROLES.includes(currentUser.empRole[0] as EmployeePageRole);

  // Initialize form with schema validation
  const form = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeFormSchema),
    defaultValues: {
      empName: '',
      empEmail: '',
      empPhone: '',
      empAvatar: '',
      empCommission: 60,
      empCheckPercent: 60,
      empCategory: [],
      empSpecialCategory: [],
      empRole: [],
      empStatus: false,
      ...employee,
    },
  });

  // Reset form when employee prop changes
  useEffect(() => {
    if (employee) {
      form.reset(employee);
    } else {
      form.reset({
        empName: '',
        empEmail: '',
        empPhone: '',
        empAvatar: '',
        empCommission: 60,
        empCheckPercent: 60,
        empCategory: [],
        empSpecialCategory: [],
        empRole: [],
        empStatus: false,
      });
    }
  }, [employee, form]);

  // Handle form submission
  const onSubmit = async (data: EmployeeFormValues) => {
    try {
      await createEditEmployee({ employee: data, id: employee?.id });
      toast.success(employee ? 'Employee updated successfully' : 'New employee added successfully');
      onClose();
      form.reset();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : employee
            ? 'Failed to update employee'
            : 'Failed to add new employee'
      );
      console.error('Failed to save employee:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-xl max-w-screen md:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{employee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
          <DialogDescription>
            {/* {employee ? 'Edit employee information' : 'Add a new employee to the system'} */}
            {'Please fill out all required fields'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Basic Information */}
              <FormField
                control={form.control}
                name="empName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Employee name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="empEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Email address" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="empPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="Phone number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="empAvatar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Avatar</FormLabel>
                    <FormControl>
                      <AvatarDropdown defaultValue={field.value} onSelect={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Only show commission fields if user has permission */}
              {canViewAuthorize && (
                <>
                  {/* Commission */}
                  <FormField
                    control={form.control}
                    name="empCommission"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Commission (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={100}
                            {...field}
                            placeholder="Enter commission (1-100)"
                            onChange={e => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Check Percent */}
                  <FormField
                    control={form.control}
                    name="empCheckPercent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Check Percent (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={100}
                            {...field}
                            placeholder="Enter check percent (0-100)"
                            onChange={e => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              {/* Categories and Roles */}
              <FormField
                control={form.control}
                name="empCategory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categories</FormLabel>
                    <FormControl>
                      <MultiSelect
                        selected={field.value}
                        options={settingsData?.serviceCategory ?? []}
                        onChange={field.onChange}
                        placeholder="Select categories"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="empSpecialCategory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Special Categories</FormLabel>
                    <FormControl>
                      <MultiSelect
                        selected={field.value}
                        options={settingsData?.specialCategory ?? []}
                        onChange={field.onChange}
                        placeholder="Select special categories"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="empRole"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Roles</FormLabel>
                    <FormControl>
                      <MultiSelect
                        selected={
                          !canViewAuthorize
                            ? (field.value?.filter(
                                role => !MANAGEMENT_ROLES.includes(role as EmployeePageRole)
                              ) ?? [])
                            : field.value
                        }
                        // Show all roles for authorized users, filter for unauthorized users
                        options={
                          !canViewAuthorize
                            ? (settingsData?.employeeRole?.filter(
                                role => !MANAGEMENT_ROLES.includes(role as EmployeePageRole)
                              ) ?? [])
                            : (settingsData?.employeeRole ?? [])
                        }
                        onChange={value => {
                          // For unauthorized users, preserve any existing restricted roles
                          if (!canViewAuthorize) {
                            const existingRestrictedRoles =
                              field.value?.filter(role =>
                                MANAGEMENT_ROLES.includes(role as EmployeePageRole)
                              ) ?? [];
                            field.onChange([...existingRestrictedRoles, ...value]);
                          } else {
                            field.onChange(value);
                          }
                        }}
                        placeholder="Select roles"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Status Switch */}
              <FormField
                control={form.control}
                name="empStatus"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-row items-center justify-between">
                      <FormLabel>Active Status</FormLabel>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-2 sticky bottom-0">
              <Button
                variant="outline"
                onClick={() => {
                  form.reset();
                  onClose();
                }}
                type="button"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isCreatingOrEditing}>
                {isCreatingOrEditing ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
